import React from "react";
import SectionCrudPage from "./SectionCrudPage";

const booleanOptions = [
  { value: true, label: "Yes" },
  { value: false, label: "No" },
];

const accessResources = [
  {
    key: "access-rules",
    label: "Access Rules",
    endpoint: "/access-rules",
    tableFields: [
      { name: "name", label: "Name" },
      { name: "rule_type", label: "Rule Type" },
      { name: "time_window_days", label: "Time Window Days" },
      { name: "entries", label: "Entries" },
    ],
    relatedTabs: [
      { key: "access-rule-zones", label: "Rule Zones", endpoint: "/access-rule-zones", foreignKey: "access_rule_id" },
      { key: "member-entitlements", label: "Entitlements", endpoint: "/member-entitlements", foreignKey: "access_rule_id" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text" },
      {
        name: "rule_type",
        label: "Rule Type",
        type: "select",
        options: [
          { value: "unlimited_time", label: "Unlimited Time" },
          { value: "entries", label: "Entries" },
          { value: "class_entries", label: "Class Entries" },
        ],
      },
      { name: "time_window_days", label: "Time Window Days", type: "number", nullable: true },
      { name: "entries", label: "Entries", type: "number", nullable: true },
    ],
  },
  {
    key: "access-rule-zones",
    label: "Access Rule Zones",
    endpoint: "/access-rule-zones",
    tableFields: [
      {
        name: "access_rule_id",
        label: "Access Rule",
        valueGetter: (row) => row?.access_rule_name || row?.access_rule?.name || row?.access_rule_id,
      },
      {
        name: "zone_id",
        label: "Zone",
        valueGetter: (row) => row?.zone_name || row?.zone?.name || row?.zone_id,
      },
    ],
    formFields: [
      {
        name: "access_rule_id",
        label: "Access Rule",
        type: "select",
        optionSource: {
          endpoint: "/access-rules",
          valueField: "id",
          labelFields: ["name", "uuid"],
        },
      },
      {
        name: "zone_id",
        label: "Zone",
        type: "select",
        optionSource: {
          endpoint: "/zones",
          valueField: "id",
          labelFields: ["name", "code", "uuid"],
        },
      },
    ],
  },
  {
    key: "member-entitlements",
    label: "Member Entitlements",
    endpoint: "/member-entitlements",
    tableFields: [
      {
        name: "user_id",
        label: "User",
        valueGetter: (row) => row?.user_name || row?.user?.name || row?.user?.email || row?.user_id,
      },
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      {
        name: "access_rule_id",
        label: "Access Rule",
        valueGetter: (row) => row?.access_rule_name || row?.access_rule?.name || row?.access_rule_id,
      },
      { name: "status", label: "Status" },
      { name: "starts_at", label: "Starts At" },
      { name: "ends_at", label: "Ends At" },
      { name: "remaining_entries", label: "Remaining Entries" },
      { name: "reserved_entries", label: "Reserved Entries" },
      { name: "granted_value", label: "Granted Value" },
      { name: "refunded_value", label: "Refunded Value" },
      { name: "value_consumed", label: "Value Consumed" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      { key: "entitlement-movements", label: "Movements", endpoint: "/entitlement-movements", foreignKey: "entitlement_id" },
    ],
    formFields: [
      {
        name: "user_id",
        label: "User",
        type: "select",
        optionSource: {
          endpoint: "/users",
          valueField: "id",
          labelFields: ["name", "email"],
        },
      },
      {
        name: "product_id",
        label: "Product",
        type: "select",
        optionSource: {
          endpoint: "/products",
          valueField: "id",
          labelFields: ["name", "code", "uuid"],
        },
      },
      {
        name: "access_rule_id",
        label: "Access Rule",
        type: "select",
        optionSource: {
          endpoint: "/access-rules",
          valueField: "id",
          labelFields: ["name", "uuid"],
        },
      },
      {
        name: "status",
        label: "Status",
        type: "select",
        options: [
          { value: "active", label: "Active" },
          { value: "paused", label: "Paused" },
          { value: "expired", label: "Expired" },
          { value: "cancelled", label: "Cancelled" },
        ],
      },
      { name: "starts_at", label: "Starts At (YYYY-MM-DD HH:MM:SS)", type: "datetime" },
      { name: "ends_at", label: "Ends At (YYYY-MM-DD HH:MM:SS)", type: "datetime", nullable: true },
      { name: "remaining_entries", label: "Remaining Entries", type: "number", nullable: true },
      { name: "reserved_entries", label: "Reserved Entries", type: "number" },
      { name: "granted_value", label: "Granted Value", type: "number" },
      { name: "refunded_value", label: "Refunded Value", type: "number" },
      { name: "value_consumed", label: "Value Consumed", type: "number" },
      {
        name: "last_refund_id",
        label: "Last Refund",
        type: "select",
        nullable: true,
        optionSource: {
          endpoint: "/sale-refunds",
          valueField: "id",
          labelFields: ["uuid", "occurred_at", "status"],
        },
      },
      {
        name: "source_type",
        label: "Source Type",
        type: "select",
        nullable: true,
        options: [
          { value: "sale", label: "Sale" },
          { value: "manual", label: "Manual" },
          { value: "adjustment", label: "Adjustment" },
          { value: "migration", label: "Migration" },
        ],
      },
      {
        name: "source_id",
        label: "Source ID",
        type: "select",
        nullable: true,
        optionSource: {
          endpoint: "/sales",
          valueField: "id",
          labelFields: ["occurred_at", "status", "uuid"],
        },
      },
    ],
  },
  {
    key: "entitlement-movements",
    label: "Entitlement Movements",
    endpoint: "/entitlement-movements",
    tableFields: [
      {
        name: "entitlement_id",
        label: "Entitlement",
        valueGetter: (row) =>
          row?.entitlement_label
          || row?.entitlement?.user_name
          || row?.entitlement?.status
          || row?.entitlement_id,
      },
      { name: "movement_type", label: "Movement Type" },
      { name: "qty", label: "Quantity" },
      { name: "reason", label: "Reason" },
      { name: "reference_type", label: "Reference Type" },
      {
        name: "reference_id",
        label: "Reference ID",
        valueGetter: (row) =>
          row?.reference_label
          || row?.reference_sale?.id
          || row?.reference_sale_refund?.id
          || row?.reference_class_booking?.id
          || row?.reference_id,
      },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    formFields: [
      {
        name: "entitlement_id",
        label: "Entitlement",
        type: "select",
        optionSource: {
          endpoint: "/member-entitlements",
          valueField: "id",
          labelFields: ["uuid", "status"],
        },
      },
      {
        name: "movement_type",
        label: "Movement Type",
        type: "select",
        options: [
          { value: "grant", label: "Grant" },
          { value: "reserve", label: "Reserve" },
          { value: "consume", label: "Consume" },
          { value: "release", label: "Release" },
          { value: "refund", label: "Refund" },
          { value: "expire", label: "Expire" },
          { value: "pause", label: "Pause" },
          { value: "resume", label: "Resume" },
        ],
      },
      { name: "qty", label: "Quantity", type: "number" },
      { name: "reason", label: "Reason", type: "text", nullable: true },
      {
        name: "reference_type",
        label: "Reference Type",
        type: "select",
        nullable: true,
        options: [
          { value: "sale", label: "Sale" },
          { value: "sale_refund", label: "Sale Refund" },
          { value: "class_booking", label: "Class Booking" },
          { value: "manual", label: "Manual" },
        ],
      },
      {
        name: "reference_id",
        label: "Reference ID",
        type: "select",
        nullable: true,
        optionSource: {
          endpoint: "/sales",
          valueField: "id",
          labelFields: ["occurred_at", "status", "uuid"],
        },
      },
    ],
  },
  {
    key: "membership-plans",
    label: "Membership Plans",
    endpoint: "/membership-plans",
    idField: "product_id",
    tableFields: [
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      { name: "billing_period", label: "Billing Period" },
      { name: "auto_renew", label: "Auto Renew" },
      { name: "freeze_allowed", label: "Freeze Allowed" },
      {
        name: "access_rule_id",
        label: "Access Rule",
        valueGetter: (row) => row?.access_rule_name || row?.access_rule?.name || row?.access_rule_id,
      },
    ],
    formFields: [
      {
        name: "product_id",
        label: "Product",
        type: "select",
        optionSource: {
          endpoint: "/products",
          valueField: "id",
          labelFields: ["name", "code", "uuid"],
        },
      },
      {
        name: "billing_period",
        label: "Billing Period",
        type: "select",
        options: [
          { value: "monthly", label: "Monthly" },
          { value: "annual", label: "Annual" },
        ],
      },
      { name: "auto_renew", label: "Auto Renew", type: "boolean", options: booleanOptions },
      { name: "freeze_allowed", label: "Freeze Allowed", type: "boolean", options: booleanOptions },
      {
        name: "access_rule_id",
        label: "Access Rule",
        type: "select",
        optionSource: {
          endpoint: "/access-rules",
          valueField: "id",
          labelFields: ["name", "uuid"],
        },
      },
    ],
  },
  {
    key: "class-packs",
    label: "Class Packs",
    endpoint: "/class-packs",
    idField: "product_id",
    tableFields: [
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      { name: "entries", label: "Entries" },
      { name: "validity_days", label: "Validity Days" },
      { name: "class_scope", label: "Class Scope" },
      {
        name: "access_rule_id",
        label: "Access Rule",
        valueGetter: (row) => row?.access_rule_name || row?.access_rule?.name || row?.access_rule_id,
      },
    ],
    relatedTabs: [
      {
        key: "class-pack-allowed-classes",
        label: "Allowed Classes",
        endpoint: "/class-pack-allowed-classes",
        foreignKey: "class_pack_product_id",
      },
    ],
    formFields: [
      {
        name: "product_id",
        label: "Product",
        type: "select",
        optionSource: {
          endpoint: "/products",
          valueField: "id",
          labelFields: ["name", "code", "uuid"],
        },
      },
      { name: "entries", label: "Entries", type: "number" },
      { name: "validity_days", label: "Validity Days", type: "number" },
      {
        name: "class_scope",
        label: "Class Scope",
        type: "select",
        options: [
          { value: "all", label: "All" },
          { value: "restricted", label: "Restricted" },
        ],
      },
      {
        name: "access_rule_id",
        label: "Access Rule",
        type: "select",
        optionSource: {
          endpoint: "/access-rules",
          valueField: "id",
          labelFields: ["name", "uuid"],
        },
      },
    ],
  },
  {
    key: "class-pack-allowed-classes",
    label: "Class Pack Allowed Classes",
    endpoint: "/class-pack-allowed-classes",
    tableFields: [
      {
        name: "class_pack_product_id",
        label: "Class Pack (Product ID)",
        valueGetter: (row) => row?.class_pack_label || row?.class_pack_product_id,
      },
      {
        name: "class_id",
        label: "Gym Class",
        valueGetter: (row) => row?.class_name || row?.gym_class?.name || row?.class_id,
      },
    ],
    formFields: [
      {
        name: "class_pack_product_id",
        label: "Class Pack (Product ID)",
        type: "select",
        optionSource: {
          endpoint: "/class-packs",
          valueField: "product_id",
          labelFields: ["product_id", "entries", "validity_days"],
        },
      },
      {
        name: "class_id",
        label: "Gym Class",
        type: "select",
        optionSource: {
          endpoint: "/gym-classes",
          valueField: "id",
          labelFields: ["name", "code", "uuid"],
        },
      },
    ],
  },
  {
    key: "services",
    label: "Services",
    endpoint: "/services",
    idField: "product_id",
    tableFields: [
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      { name: "duration_minutes", label: "Duration Minutes" },
      { name: "requires_scheduling", label: "Requires Scheduling" },
    ],
    formFields: [
      {
        name: "product_id",
        label: "Product",
        type: "select",
        optionSource: {
          endpoint: "/products",
          valueField: "id",
          labelFields: ["name", "code", "uuid"],
        },
      },
      { name: "duration_minutes", label: "Duration Minutes", type: "number", nullable: true },
      { name: "requires_scheduling", label: "Requires Scheduling", type: "boolean", options: booleanOptions },
    ],
  },
  {
    key: "fees",
    label: "Fees",
    endpoint: "/fees",
    idField: "product_id",
    tableFields: [
      {
        name: "product_id",
        label: "Product",
        valueGetter: (row) => row?.product_name || row?.product?.name || row?.product_id,
      },
      { name: "fee_kind", label: "Fee Kind" },
      { name: "mandatory_on_signup", label: "Mandatory On Signup" },
      { name: "validity_days", label: "Validity Days" },
    ],
    formFields: [
      {
        name: "product_id",
        label: "Product",
        type: "select",
        optionSource: {
          endpoint: "/products",
          valueField: "id",
          labelFields: ["name", "code", "uuid"],
        },
      },
      {
        name: "fee_kind",
        label: "Fee Kind",
        type: "select",
        options: [
          { value: "signup", label: "Signup" },
          { value: "insurance", label: "Insurance" },
          { value: "other", label: "Other" },
        ],
      },
      { name: "mandatory_on_signup", label: "Mandatory On Signup", type: "boolean", options: booleanOptions },
      { name: "validity_days", label: "Validity Days", type: "number", nullable: true },
    ],
  },
];

export default function AccessPage() {
  return (
    <SectionCrudPage
      title="Access & Entitlements"
      subtitle="Manage access rules, entitlements, packs, memberships, services and fees."
      resources={accessResources}
    />
  );
}
